import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

// Eagerly load Index for faster LCP on landing page
import Index from "./pages/Index";

// Lazy load other pages for code splitting - improves initial bundle size
const AuthPage = lazy(() => import("./pages/AuthPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProfilRepreneur = lazy(() => import("./pages/ProfilRepreneur"));
const CreerDossier = lazy(() => import("./pages/CreerDossier"));
const ScanWizard = lazy(() => import("./pages/ScanWizard"));
const Resultat = lazy(() => import("./pages/Resultat"));
const BusinessAnalysis = lazy(() => import("./pages/BusinessAnalysis"));
const FinancialAnalysis = lazy(() => import("./pages/FinancialAnalysis"));
const Synthesis = lazy(() => import("./pages/Synthesis"));
const SharedSynthesis = lazy(() => import("./pages/SharedSynthesis"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Support = lazy(() => import("./pages/Support"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const CGU = lazy(() => import("./pages/CGU"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const queryClient = new QueryClient();

// Loading spinner element (not a component) to avoid forwardRef warnings in Suspense fallback
const loadingSpinner = (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return loadingSpinner;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={loadingSpinner}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/share/:token" element={<SharedSynthesis />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
        <Route path="/cgu" element={<CGU />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profil" element={<ProtectedRoute><ProfilRepreneur /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
        <Route path="/admin/support" element={<ProtectedRoute><AdminSupport /></ProtectedRoute>} />
        <Route path="/nouveau-dossier" element={<ProtectedRoute><CreerDossier /></ProtectedRoute>} />
        <Route path="/dossier/nouveau" element={<ProtectedRoute><CreerDossier /></ProtectedRoute>} />
        <Route path="/scan/:dossierId" element={<ProtectedRoute><ScanWizard /></ProtectedRoute>} />
        <Route path="/resultat/:dossierId" element={<ProtectedRoute><Resultat /></ProtectedRoute>} />
        <Route path="/business/:dossierId" element={<ProtectedRoute><BusinessAnalysis /></ProtectedRoute>} />
        <Route path="/financial/:dossierId" element={<ProtectedRoute><FinancialAnalysis /></ProtectedRoute>} />
        <Route path="/synthesis/:dossierId" element={<ProtectedRoute><Synthesis /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
